import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import "./ModalShell.css";

/**
 * Shared layout for form and detail modals (not for simple confirms — use ConfirmationModal).
 * Provides overlay, title, scrollable body, optional footer, Escape and overlay close.
 */
function ModalShell({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth = 560,
  zIndex = 1080,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  className = "",
  overlayClassName = "",
  bodyClassName = "",
  footerClassName = "",
  panelRef,
  bodyProps,
  titleId: titleIdProp,
}) {
  const genId = useId();
  const titleId = titleIdProp || `modal-shell-title-${genId}`;

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (onClose) onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  const showHeader = !!(title || subtitle || onClose);

  // Portal so overlays stay visible when opened from menus/popovers (e.g. row action ⋮).
  return createPortal(
    <div
      className={`modal-shell-overlay ${overlayClassName}`.trim()}
      style={{ zIndex }}
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget && onClose) onClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={`modal-shell-panel ${className}`.trim()}
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (
          <header className="modal-shell-header">
            <div className="modal-shell-header-main">
              {title && (
                <h2 id={titleId} className="modal-shell-title">
                  {title}
                </h2>
              )}
              {subtitle && <p className="modal-shell-subtitle">{subtitle}</p>}
            </div>
            {onClose && (
              <button
                type="button"
                className="modal-shell-close"
                onClick={onClose}
                aria-label="Close dialog"
              >
                ×
              </button>
            )}
          </header>
        )}
        <div className={`modal-shell-body ${bodyClassName}`.trim()} {...(bodyProps || {})}>
          {children}
        </div>
        {footer != null && footer !== false && (
          <footer className={`modal-shell-footer ${footerClassName}`.trim()}>{footer}</footer>
        )}
      </div>
    </div>,
    document.body
  );
}

export default ModalShell;
